import { Link } from "react-router-dom";
import { NonWorkspacePage, PageEmptyState, PageErrorState, PageLoadingState } from "../components/layout/NonWorkspacePage";
import { OperatorSurface } from "../components/ui/OperatorSurface";
import { OperatorBrandGate } from "../components/layout/OperatorBrandGate";
import { useActiveBrand } from "../hooks/useActiveBrand";
import { useInbox } from "../hooks/useInbox";
import { inboxItemRoute } from "../lib/inbox-selectors";
import { buildBrandScopedPath } from "../lib/brand-navigation";

export function Inbox() {
  const { activeBrand, activeBrandId } = useActiveBrand();
  const brandId = activeBrandId ?? activeBrand?.id;
  const { items, isLoading, status, refetch } = useInbox(brandId);
  if (!brandId)
    return (
      <OperatorBrandGate
        title="Choose a brand before opening Inbox"
        description="Inbox actions belong to one brand."
      />
    );
  return (
    <NonWorkspacePage
      width="standard"
      header={{
        eyebrow: "Inbox",
        title: "Inbox",
        // Only a successful load may claim "nothing needs attention" (UI-PRINCIPLES P3).
        description: (
          isLoading
            ? "Checking what needs your attention…"
            : status === "error"
              ? "Inbox could not be loaded."
              : items.length
                ? `${items.length} item${items.length === 1 ? "" : "s"} need your attention.`
                : "Nothing needs your attention right now."
        ),
        breadcrumbs: [
          { label: "Workspace", href: "/home" },
          { label: "Inbox" },
        ]
      }}
    >
      {isLoading ? (
        <PageLoadingState>Loading Inbox…</PageLoadingState>
      ) : status === "error" ? (
        <PageErrorState description="We could not load Inbox." action={<button type="button" className="font-medium text-red-900 underline" onClick={() => void refetch()}>Try again</button>} />
      ) : items.length === 0 ? (
        <PageEmptyState
          title="Nothing needs your attention"
          description="You can keep an eye on agent progress in Runs."
          action={<Link
            className="font-medium text-brand-700 underline"
            to={buildBrandScopedPath("/runs", brandId)}
          >
            View activity
          </Link>}
        />
      ) : (
        <div className="space-y-3">
          {items.map((item) => (
            <OperatorSurface key={item.id} density="compact" className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
              <div className="min-w-0">
                <h2 className="text-base font-semibold text-slate-900">{item.title}</h2>
                <p className="mt-1 text-sm leading-6 text-slate-700">{item.description}</p>
              </div>
              <Link
                to={buildBrandScopedPath(inboxItemRoute(item, brandId), brandId)}
                className="shrink-0 text-sm font-medium text-brand-700 hover:text-brand-800 focus:outline-none focus:ring-2 focus:ring-brand-500 focus:ring-offset-2"
              >
                {item.actionLabel}
              </Link>
            </OperatorSurface>
          ))}
        </div>
      )}
    </NonWorkspacePage>
  );
}
